import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { affiliateService, ProgramSettings } from '../../services/affiliateService';

interface AffiliateSettingsProps {
  programId?: string | null;
}

interface CommissionStructure {
  id: string;
  name: string;
  type: 'percentage' | 'fixed';
  value: number;
  productType: string;
  isActive: boolean;
}

// Sample data for the placeholder
const sampleCommissionStructures: CommissionStructure[] = [
  {
    id: '1',
    name: 'Standard Commission',
    type: 'percentage',
    value: 10,
    productType: 'All Products',
    isActive: true
  },
  {
    id: '2',
    name: 'Premium Commission',
    type: 'percentage',
    value: 15,
    productType: 'Premium Plans',
    isActive: true
  },
  {
    id: '3',
    name: 'Special Promotion',
    type: 'fixed',
    value: 50,
    productType: 'Annual Subscriptions',
    isActive: false
  }
];

export const AffiliateSettings: React.FC<AffiliateSettingsProps> = ({ programId }) => {
  const [programName, setProgramName] = useState('Explanatum Affiliate Program');
  const [programDescription, setProgramDescription] = useState('Join our affiliate program and earn commissions by referring customers to Explanatum.');
  const [cookieDuration, setCookieDuration] = useState(30);
  const [minimumPayout, setMinimumPayout] = useState(100);
  const [payoutMethods, setPayoutMethods] = useState<string[]>(['PayPal', 'Bank Transfer']);
  const [commissions, setCommissions] = useState<CommissionStructure[]>([]);
  const [newCommission, setNewCommission] = useState<Partial<CommissionStructure>>({
    name: '',
    type: 'percentage',
    value: 10,
    productType: '',
    isActive: true
  });
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Fetch settings from API
  const fetchSettings = async () => {
    setLoading(true);
    try {
      if (!programId) {
        // No program yet: keep placeholder data and avoid network call
        setCommissions(sampleCommissionStructures);
        return;
      }
      const data: ProgramSettings = await affiliateService.getProgramSettings(programId);
      
      // Update state with fetched settings
      setProgramName(data.program_name || 'Explanatum Affiliate Program');
      setProgramDescription(data.program_description || 'Join our affiliate program and earn commissions by referring customers to Explanatum.');
      setCookieDuration(data.cookie_duration || 30);
      setMinimumPayout(data.minimum_payout || 100);
      setPayoutMethods(data.payout_methods || ['PayPal', 'Bank Transfer']);
      
      // Format commission structures
      if (data.commission_structures && Array.isArray(data.commission_structures)) {
        const formattedCommissions: CommissionStructure[] = data.commission_structures.map((c: any) => ({
          id: c.id,
          name: c.name,
          type: c.type,
          value: c.value,
          productType: c.product_type,
          isActive: c.is_active
        }));
        setCommissions(formattedCommissions);
      } else {
        setCommissions(sampleCommissionStructures);
      }
    } catch (error) {
      console.error('Error fetching affiliate settings:', error);
      toast.error('Failed to load settings');
      
      // Fall back to sample data
      setCommissions(sampleCommissionStructures);
    } finally {
      setLoading(false);
    }
  };
  
  // Load settings when component mounts
  useEffect(() => {
    fetchSettings();
    // Re-fetch when programId changes (e.g., after creating a program)
  }, [programId]);
  
  // Save settings to API
  const saveSettings = async () => {
    setSaving(true);
    try {
      if (!programId) {
        throw new Error('No program to save settings for');
      }
      
      // Format data for API
      const settingsData: ProgramSettings = {
        program_name: programName,
        program_description: programDescription,
        cookie_duration: cookieDuration,
        minimum_payout: minimumPayout,
        payout_methods: payoutMethods,
        commission_structures: commissions.map(c => ({
          id: c.id,
          name: c.name,
          type: c.type,
          value: c.value,
          product_type: c.productType,
          is_active: c.isActive
        }))
      };
      
      await affiliateService.updateProgramSettings(programId, settingsData);

      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving affiliate settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePayoutMethodToggle = (method: string) => {
    setPayoutMethods(prev => {
      if (prev.includes(method)) {
        return prev.filter(m => m !== method);
      } else {
        return [...prev, method];
      }
    });
  };

  const handleAddCommission = () => {
    if (!newCommission.name || !newCommission.productType) {
      toast.error('Please fill in all fields');
      return;
    }

    const newCommissionComplete = {
      ...newCommission,
      id: `comm-${Date.now()}`,
      type: newCommission.type || 'percentage',
      value: newCommission.value || 10,
      isActive: newCommission.isActive !== undefined ? newCommission.isActive : true
    } as CommissionStructure;

    setCommissions([...commissions, newCommissionComplete]);
    setNewCommission({
      name: '',
      type: 'percentage',
      value: 10,
      productType: '',
      isActive: true
    });
    toast.success('Commission structure added');
  };

  const handleToggleCommission = (id: string) => {
    setCommissions(prev => 
      prev.map(comm => 
        comm.id === id ? { ...comm, isActive: !comm.isActive } : comm
      )
    );
  };

  const handleRemoveCommission = (id: string) => {
    setCommissions(prev => prev.filter(comm => comm.id !== id));
    toast.success('Commission structure removed');
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      await saveSettings();
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  // Display loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Program Settings</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Configure your affiliate program settings.
          </p>
        </div>
        
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          {!programId && (
            <div className="mb-6 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
              You don’t have a program yet. You can review placeholder settings here. Create a program to enable saving and API-backed settings.
            </div>
          )}
          <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8">
            <div className="sm:col-span-2">
              <label htmlFor="program-name" className="block text-sm font-medium text-gray-700">
                Program Name
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="program-name"
                  id="program-name"
                  value={programName}
                  onChange={(e) => setProgramName(e.target.value)}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
              </div>
            </div>
            
            <div className="sm:col-span-2">
              <label htmlFor="program-description" className="block text-sm font-medium text-gray-700">
                Program Description
              </label>
              <div className="mt-1">
                <textarea
                  id="program-description"
                  name="program-description"
                  rows={3}
                  value={programDescription}
                  onChange={(e) => setProgramDescription(e.target.value)}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                This description will be visible to potential affiliates on your signup page.
              </p>
            </div>
            
            <div>
              <label htmlFor="cookie-duration" className="block text-sm font-medium text-gray-700">
                Cookie Duration (days)
              </label>
              <div className="mt-1">
                <input
                  type="number"
                  name="cookie-duration"
                  id="cookie-duration"
                  min="1"
                  max="365"
                  value={cookieDuration}
                  onChange={(e) => setCookieDuration(parseInt(e.target.value))}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                How long the affiliate cookie will last after a visitor clicks an affiliate link.
              </p>
            </div>
            
            <div>
              <label htmlFor="minimum-payout" className="block text-sm font-medium text-gray-700">
                Minimum Payout Amount (GBP)
              </label>
              <div className="mt-1">
                <input
                  type="number"
                  name="minimum-payout"
                  id="minimum-payout"
                  min="1"
                  value={minimumPayout}
                  onChange={(e) => setMinimumPayout(parseInt(e.target.value))}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Minimum amount required before an affiliate can request a payout.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Commission Structures</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Define the commission rates for your affiliate program.
          </p>
        </div>
        
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {commissions.map((commission) => (
                    <tr key={commission.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {commission.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {commission.type === 'percentage' ? 'Percentage' : 'Fixed Amount'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {commission.type === 'percentage' ? `${commission.value}%` : `£${commission.value}`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {commission.productType}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${commission.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {commission.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleToggleCommission(commission.id)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          {commission.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleRemoveCommission(commission.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="mt-6 bg-gray-50 p-4 rounded-md">
              <h4 className="text-base font-medium text-gray-900 mb-4">Add New Commission Structure</h4>
              <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-6 sm:gap-x-4">
                <div className="sm:col-span-2">
                  <label htmlFor="new-commission-name" className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    id="new-commission-name"
                    value={newCommission.name}
                    onChange={(e) => setNewCommission({...newCommission, name: e.target.value})}
                    className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                
                <div className="sm:col-span-1">
                  <label htmlFor="new-commission-type" className="block text-sm font-medium text-gray-700">Type</label>
                  <select
                    id="new-commission-type"
                    value={newCommission.type}
                    onChange={(e) => setNewCommission({
                      ...newCommission, 
                      type: e.target.value as 'percentage' | 'fixed'
                    })}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>
                
                <div className="sm:col-span-1">
                  <label htmlFor="new-commission-value" className="block text-sm font-medium text-gray-700">
                    Value {newCommission.type === 'percentage' ? '(%)' : '(GBP)'}
                  </label>
                  <input
                    type="number"
                    id="new-commission-value"
                    min="0"
                    step={newCommission.type === 'percentage' ? '0.01' : '1'}
                    value={newCommission.value}
                    onChange={(e) => setNewCommission({...newCommission, value: parseFloat(e.target.value)})}
                    className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                
                <div className="sm:col-span-2">
                  <label htmlFor="new-commission-product" className="block text-sm font-medium text-gray-700">Product Type</label>
                  <input
                    type="text"
                    id="new-commission-product"
                    value={newCommission.productType}
                    onChange={(e) => setNewCommission({...newCommission, productType: e.target.value})}
                    className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    placeholder="e.g., All Products, Premium Plans"
                  />
                </div>
                
                <div className="sm:col-span-6">
                  <button
                    type="button"
                    onClick={handleAddCommission}
                    className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Add Commission Structure
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Payment Methods</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Configure available payout methods for your affiliates.
          </p>
        </div>
        
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          <fieldset>
            <legend className="text-base font-medium text-gray-900">Select enabled payment methods</legend>
            <div className="mt-4 space-y-4">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="paypal"
                    name="paypal"
                    type="checkbox"
                    checked={payoutMethods.includes('PayPal')}
                    onChange={() => handlePayoutMethodToggle('PayPal')}
                    className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="paypal" className="font-medium text-gray-700">PayPal</label>
                  <p className="text-gray-500">Enable PayPal as a payout method for your affiliates.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="bank-transfer"
                    name="bank-transfer"
                    type="checkbox"
                    checked={payoutMethods.includes('Bank Transfer')}
                    onChange={() => handlePayoutMethodToggle('Bank Transfer')}
                    className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="bank-transfer" className="font-medium text-gray-700">Bank Transfer</label>
                  <p className="text-gray-500">Enable bank transfers as a payout method.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="stripe"
                    name="stripe"
                    type="checkbox"
                    checked={payoutMethods.includes('Stripe')}
                    onChange={() => handlePayoutMethodToggle('Stripe')}
                    className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="stripe" className="font-medium text-gray-700">Stripe</label>
                  <p className="text-gray-500">Enable Stripe as a payout method (requires Stripe Connect).</p>
                </div>
              </div>
            </div>
          </fieldset>
        </div>
      </div>
      
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSaveSettings}
          disabled={isSaving || !programId}
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          {isSaving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default AffiliateSettings;
